import { useEffect, useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill, ForwardStepper, OrderTimeline } from "@/components/OrderBits";
import { isReturn, inr } from "@/lib/orderFlow";
import { printInvoice, printLabel } from "@/lib/orderDocs";

function toBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

const TABS = [
  { k: "new", label: "New", match: (s) => s === "admin_verified" },
  { k: "packing", label: "Packing", match: (s) => s === "packing" },
  { k: "ready", label: "Ready", match: (s) => s === "ready_to_dispatch" },
  { k: "shipped", label: "Shipped", match: (s) => ["dispatched", "in_transit", "out_for_delivery"].includes(s) },
  { k: "delivered", label: "Delivered", match: (s) => s === "delivered" },
  { k: "flagged", label: "Flagged", match: (s) => s === "fulfilment_hold" },
  { k: "returns", label: "Returns", match: (s) => isReturn(s) },
  { k: "rto", label: "RTO", match: (s) => ["rto_in_transit", "rto_delivered"].includes(s) },
  { k: "completed", label: "Completed", match: (s) => ["completed", "refunded", "credit_issued"].includes(s) },
];

function VendorDecisionDialog({ order, open, onClose, onSaved }) {
  const [approve, setApprove] = useState(true);
  const [notes, setNotes] = useState("");
  const submit = async () => {
    try {
      await api.post(`/vendor/orders/${order.id}/return-decision`, { approve, notes });
      toast.success(approve ? "Return approved" : "Return rejected — buyer can escalate to admin");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!order) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-2 border-foreground rounded-none bg-background">
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Return Decision · № {order.id.slice(0, 8).toUpperCase()}</p>
          <DialogTitle className="font-serif text-3xl tracking-tighter">Approve or reject.</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="vd-approve" onClick={() => setApprove(true)}
              className={`p-3 border font-mono text-xs uppercase ${approve ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-[hsl(var(--accent))]" : "border-foreground/40"}`}>Approve Return</button>
            <button data-testid="vd-reject" onClick={() => setApprove(false)}
              className={`p-3 border font-mono text-xs uppercase ${!approve ? "bg-destructive text-destructive-foreground border-destructive" : "border-foreground/40"}`}>Reject Return</button>
          </div>
          <textarea data-testid="vd-notes" placeholder="Inspection notes..." value={notes} onChange={(e) => setNotes(e.target.value)} className="underline-input min-h-[80px] w-full" />
          <button data-testid="vd-submit" onClick={submit} className="w-full bg-foreground text-background py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))]">Confirm Decision</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DisputeDialog({ order, open, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [images, setImages] = useState([]);
  const fileRef = useRef(null);
  const addFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const b64s = await Promise.all(files.map(toBase64));
    setImages((s) => [...s, ...b64s]);
    e.target.value = "";
  };
  const submit = async () => {
    if (!reason.trim() || images.length === 0) { toast.error("Reason + evidence images required"); return; }
    try {
      await api.post(`/vendor/orders/${order.id}/dispute`, { reason, evidence_images: images });
      toast.success("Dispute raised — Spotters Hub will review");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!order) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-2 border-destructive rounded-none bg-background">
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-widest text-destructive">Raise Dispute · № {order.id.slice(0, 8).toUpperCase()}</p>
          <DialogTitle className="font-serif text-3xl tracking-tighter">Flag it for admin review.</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <textarea data-testid="disp-reason" placeholder="e.g. Product returned damaged / used / missing accessories..." value={reason} onChange={(e) => setReason(e.target.value)} className="underline-input min-h-[80px] w-full" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Evidence photos *</p>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {images.map((src, i) => (
                <div key={i} className="relative aspect-square bg-secondary">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setImages((s) => s.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-foreground text-background w-5 h-5 text-xs">×</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => fileRef.current?.click()} className="border border-foreground py-2 px-4 font-mono text-xs uppercase">Upload Photos</button>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={addFiles} className="hidden" />
          </div>
          <button data-testid="disp-submit" onClick={submit} className="w-full bg-destructive text-destructive-foreground py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background">Raise Dispute</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const REASONS = ["Out of stock", "Item damaged / defective", "Pricing error", "Can't ship to this address", "Other"];

function CantFulfilDialog({ order, open, onClose, onSaved }) {
  const [choice, setChoice] = useState("Out of stock");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  if (!order) return null;
  const submit = async () => {
    const reason = choice === "Other" ? detail.trim() : (detail.trim() ? `${choice} — ${detail.trim()}` : choice);
    if (!reason) { toast.error("Please add a short reason."); return; }
    setBusy(true);
    try {
      await api.post(`/vendor/orders/${order.id}/cant-fulfil`, { reason });
      toast.success("Flagged for the Spotters team — they'll take it from here.");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-2 border-foreground rounded-none bg-background">
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Can't fulfil · № {order.id.slice(0, 8).toUpperCase()}</p>
          <DialogTitle className="font-serif text-3xl tracking-tighter">Flag for the Spotters team.</DialogTitle>
        </DialogHeader>
        <p className="mt-2 text-sm text-muted-foreground">This won't cancel the order or notify the buyer. Our team will try to fulfil it another way, or reach out to you, before deciding.</p>
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            {REASONS.map((r) => (
              <button key={r} type="button" onClick={() => setChoice(r)}
                className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest ${choice === r ? "bg-foreground text-background border-foreground" : "border-foreground/30 hover:border-foreground"}`}>{r}</button>
            ))}
          </div>
          <textarea placeholder={choice === "Other" ? "Describe the issue…" : "Add any detail (optional)…"} value={detail} onChange={(e) => setDetail(e.target.value)} className="underline-input min-h-[70px] w-full" />
          <button disabled={busy} onClick={submit} className="w-full bg-foreground text-background py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">{busy ? "Flagging…" : "Flag this order"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function VendorOrders() {
  const { vendor } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("new");
  const [openId, setOpenId] = useState(null);
  const [decision, setDecision] = useState(null);
  const [dispute, setDispute] = useState(null);
  const [cantFulfil, setCantFulfil] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => setOrders(await api.get("/vendor/orders").then(r => r.data));
  useEffect(() => { load(); }, []);

  const act = async (id, path, okMsg) => {
    setBusy(id + path);
    try {
      await api.post(`/vendor/orders/${id}/${path}`);
      toast.success(okMsg);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  // Open the official courier label (the Shiprocket PDF the vendor pastes on the
  // parcel). Falls back to the internal reference label for mock shipments.
  // Print the tax invoice; if the vendor is on manual numbering and this order
  // isn't numbered yet, ask for the number first.
  const openInvoice = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.id}/invoice`);
      if (data.invoice_pending && data.invoice_mode === "manual") {
        const n = window.prompt("Enter the invoice number for this order:", data.seller?.invoice_hint || "");
        if (!n || !n.trim()) { toast.message("Invoice number is required to print."); return; }
        await api.post(`/orders/${o.id}/invoice-no`, { invoice_no: n.trim() });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't prepare the invoice"); return; }
    printInvoice(o);
  };

  const openCourierLabel = async (o) => {
    setBusy(o.id + "label");
    try {
      const { data } = await api.get(`/orders/${o.id}/shipping-label`);
      if (data.label_url) { window.open(data.label_url, "_blank", "noopener,noreferrer"); return; }
      if (data.mocked) { printLabel(o, vendor.store_name); return; }
      toast.message("Courier label is still generating — try again in a few seconds, or download it from your Shiprocket dashboard.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't fetch the courier label");
    } finally { setBusy(null); }
  };

  const counts = TABS.reduce((m, t) => ((m[t.k] = orders.filter((o) => t.match(o.status)).length), m), {});
  const activeTab = TABS.find((t) => t.k === tab);
  const shown = orders.filter((o) => activeTab.match(o.status));

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ledger</p>
          <h1 className="font-serif text-6xl mt-2 mb-6 tracking-tighter">Orders.</h1>
        </div>
        {tab === "ready" && (
          <button onClick={async () => {
            try {
              const { data } = await api.get("/vendor/manifest");
              if (data.manifest_url) window.open(data.manifest_url, "_blank", "noopener,noreferrer");
              else toast.message(data.message || "No booked shipments to manifest yet.");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't generate manifest"); }
          }} className="border border-foreground px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background whitespace-nowrap mt-1" title="Courier handover sheet (Shiprocket) for your ready-to-dispatch shipments">Download manifest</button>
        )}
      </div>

      <div className="flex gap-4 md:gap-6 border-b border-foreground mb-8 font-mono text-[11px] uppercase tracking-widest overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`vtab-${t.k}`} onClick={() => setTab(t.k)}
            className={`py-3 border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${tab === t.k ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-transparent hover:text-[hsl(var(--accent))]"}`}>
            {t.label}
            {counts[t.k] > 0 && <span className="bg-foreground text-background px-1.5 py-0.5 text-[9px]">{counts[t.k]}</span>}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="border border-foreground/30 p-8">
          <p className="font-serif text-2xl text-muted-foreground">Nothing in {activeTab.label.toLowerCase()}.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {shown.map((o) => {
            const myItems = o.items.filter((it) => it.vendor_id === vendor.id);
            const myTotal = myItems.reduce((a, it) => a + it.price * it.quantity, 0);
            const expanded = openId === o.id;
            const b = (p) => busy === o.id + p;
            return (
              <div key={o.id} data-testid={`vendor-order-${o.id}`} className="border border-foreground p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Order № {o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString("en-IN")}</p>
                    <p className="font-serif text-xl mt-1">{o.buyer_name}</p>
                    <p className="text-[11px] font-mono text-muted-foreground">Ship to: {o.address?.city}, {o.address?.state} — {o.address?.pincode}</p>
                  </div>
                  <div className="text-right">
                    <StatusPill status={o.status} />
                    <p className="font-serif text-xl mt-1">{inr(myTotal)}</p>
                  </div>
                </div>

                <div className="mt-4"><ForwardStepper status={o.status} /></div>

                <div className="mt-4 border-t border-foreground/20 pt-3 space-y-2">
                  {myItems.map((it, i) => (
                    <div key={`${o.id}-${it.product_id}-${it.size}-${i}`} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-secondary overflow-hidden">{it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}</div>
                      <div className="flex-1">
                        <p className="font-serif text-sm">{it.title}</p>
                        <p className="text-[11px] font-mono text-muted-foreground">Size {it.size} · Qty {it.quantity}</p>
                      </div>
                      <p className="font-mono text-xs">{inr(it.price * it.quantity)}</p>
                    </div>
                  ))}
                </div>

                {o.awb && (
                  <p className="mt-3 font-mono text-[11px] text-muted-foreground">AWB <span className="text-foreground">{o.awb}</span>{o.courier_name ? ` · ${o.courier_name}` : ""}{o.shipment_mocked ? " (simulated)" : ""}</p>
                )}

                {o.return_reason && isReturn(o.status) && (
                  <div className="mt-4 border-t border-destructive/40 pt-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-destructive">Buyer's Return</p>
                    <p className="text-sm mt-1 italic">"{o.return_reason}"</p>
                    {o.return_images?.length > 0 && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {o.return_images.map((src, i) => (
                          <a key={i} href={src} target="_blank" rel="noreferrer" className="w-16 h-16 bg-secondary overflow-hidden">
                            <img src={src} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Forward + return actions */}
                <div className="mt-4 flex gap-2 flex-wrap items-center">
                  {o.status === "admin_verified" && (
                    <button disabled={b("accept")} onClick={() => act(o.id, "accept", "Packing started")}
                      className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">Accept &amp; pack</button>
                  )}
                  {/* Invoice + courier label — from packing onward (generating the
                      label books the Shiprocket shipment). Hidden for returns. */}
                  {(o.status === "packing" || o.status === "ready_to_dispatch" || o.awb) && !isReturn(o.status) && (
                    <>
                      <button onClick={() => openInvoice(o)}
                        className="border border-foreground px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background">Invoice</button>
                      <button disabled={b("label")} onClick={() => openCourierLabel(o)}
                        className="border border-foreground px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50">Shipping label</button>
                    </>
                  )}
                  {o.status === "packing" && (
                    <button disabled={b("ready")} onClick={() => act(o.id, "ready", "Marked ready for dispatch")}
                      className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-4 py-2 text-xs font-mono uppercase tracking-widest disabled:opacity-50">Mark ready</button>
                  )}
                  {o.status === "ready_to_dispatch" && (
                    <button disabled={b("handover")} onClick={() => act(o.id, "handover", "Handed to courier — shipment booked")}
                      className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">Hand to courier</button>
                  )}
                  {["admin_verified", "packing", "ready_to_dispatch"].includes(o.status) && (
                    <button onClick={() => setCantFulfil(o)}
                      className="border border-destructive text-destructive px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground">Can't fulfil</button>
                  )}
                  {o.status === "fulfilment_hold" && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground border border-foreground/30 px-3 py-2">Flagged — with Spotters team</span>
                  )}
                  {o.status === "rto_in_transit" && (
                    <button disabled={b("rto-received")} onClick={() => act(o.id, "rto-received", "RTO parcel received")}
                      className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">RTO received</button>
                  )}
                  {o.status === "return_shipped" && (
                    <button onClick={() => act(o.id, "return-received", "Marked received — start inspection")}
                      className="bg-foreground text-background px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-[hsl(var(--accent))]">Mark received</button>
                  )}
                  {o.status === "return_received" && (
                    <>
                      <button onClick={() => setDecision(o)} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-4 py-2 text-xs font-mono uppercase tracking-widest">Approve / Reject</button>
                      <button onClick={() => setDispute(o)} className="border border-destructive text-destructive px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground">Raise dispute</button>
                    </>
                  )}
                  {o.status === "return_vendor_rejected" && (
                    <button onClick={() => setDispute(o)} className="border border-destructive text-destructive px-4 py-2 text-xs font-mono uppercase tracking-widest">Raise dispute</button>
                  )}
                  <button onClick={() => setOpenId(expanded ? null : o.id)}
                    className="ml-auto font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">{expanded ? "Hide history" : "History"}</button>
                </div>

                {expanded && (
                  <div className="mt-4 border-t border-foreground/20 pt-3">
                    <OrderTimeline timeline={o.timeline} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <VendorDecisionDialog order={decision} open={!!decision} onClose={() => setDecision(null)} onSaved={load} />
      <DisputeDialog order={dispute} open={!!dispute} onClose={() => setDispute(null)} onSaved={load} />
      <CantFulfilDialog order={cantFulfil} open={!!cantFulfil} onClose={() => setCantFulfil(null)} onSaved={load} />
    </div>
  );
}
